import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { OnboardingApiService } from '../../services/onboarding-api.service';
import { StateManagementService } from '../../services/state-management.service';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatIconModule,
    MatCheckboxModule,
  ],
  template: `
    <div class="auth-page">
      <mat-card class="auth-card">
        <div class="auth-logo">
          <img src="assets/NewLogo.webp" alt="Chareety">
          <h2>Nonprofit Onboarding</h2>
          <p>Set up your donation page & payment processing</p>
        </div>

        <!-- Verification in progress -->
        <div *ngIf="isVerifying" class="verification-container">
          <mat-spinner diameter="48"></mat-spinner>
          <p class="verification-message">Verifying your organization...</p>
        </div>

        <!-- Verification error -->
        <div *ngIf="verificationError" class="verification-error">
          <mat-icon>error</mat-icon>
          <p>{{ verificationError }}</p>
          <button mat-stroked-button color="primary" (click)="registerForm.reset(); verificationError = null;">
            Return to Registration
          </button>
        </div>

        <!-- Registration form (hidden during verification) -->
        <form *ngIf="!isVerifying && !verificationError" [formGroup]="registerForm" (ngSubmit)="onSubmit()">
          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Organization Name</mat-label>
              <input matInput formControlName="orgName" placeholder="e.g. Community Food Bank">
              <mat-error *ngIf="hasError('orgName', 'required')">Organization name is required</mat-error>
            </mat-form-field>
          </div>

          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Admin Email</mat-label>
              <input matInput formControlName="email" type="email" placeholder="your.email@nonprofit.org">
              <mat-error *ngIf="hasError('email', 'required')">Email is required</mat-error>
              <mat-error *ngIf="hasError('email', 'email')">Enter a valid email</mat-error>
            </mat-form-field>
          </div>

          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Password</mat-label>
              <input matInput formControlName="password" type="password" placeholder="At least 8 characters">
              <mat-error *ngIf="hasError('password', 'required')">Password is required</mat-error>
              <mat-error *ngIf="hasError('password', 'minlength')">Password must be at least 8 characters</mat-error>
            </mat-form-field>
          </div>

          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Confirm Password</mat-label>
              <input matInput formControlName="confirmPassword" type="password" placeholder="Re-enter your password">
              <mat-error *ngIf="hasError('confirmPassword', 'required')">Please confirm your password</mat-error>
              <mat-error *ngIf="registerForm.hasError('passwordMismatch')">Passwords do not match</mat-error>
            </mat-form-field>
          </div>

          <div class="consent-section">
            <mat-checkbox formControlName="agreeTerms" color="primary">
              I agree to the
              <a href="https://chareety.com/terms" target="_blank" rel="noopener"
                 (click)="$event.stopPropagation()">Terms of Service</a>
              and
              <a href="https://chareety.com/privacy" target="_blank" rel="noopener"
                 (click)="$event.stopPropagation()">Privacy Policy</a>
            </mat-checkbox>
            <div class="consent-error"
                 *ngIf="registerForm.get('agreeTerms')?.touched && hasError('agreeTerms', 'requiredTrue')">
              You must agree to the Terms of Service and Privacy Policy
            </div>

            <mat-checkbox formControlName="agreeOrgContract" color="primary">
              I have read and agree to the
              <a href="https://chareety.com/msa" target="_blank" rel="noopener"
                 (click)="$event.stopPropagation()">Master Service Agreement</a>
            </mat-checkbox>
            <div class="consent-error"
                 *ngIf="registerForm.get('agreeOrgContract')?.touched && hasError('agreeOrgContract', 'requiredTrue')">
              You must agree to the Organization Service Agreement
            </div>
          </div>

          <button mat-raised-button color="primary" type="submit" class="full-width" [disabled]="loading || !registerForm.valid">
            <mat-spinner *ngIf="loading" diameter="20" class="btn-spinner"></mat-spinner>
            <span>{{ loading ? 'Creating Account...' : 'Create Account & Start Onboarding' }}</span>
          </button>
        </form>

        <div class="auth-link-row">
          Already have an account? <a routerLink="/login">Sign in</a>
        </div>
      </mat-card>
    </div>
  `,
  styles: [`
    .auth-page {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg,
        var(--chareety-primary) 0%,
        var(--chareety-primary-dark) 100%);
      padding: 24px;
    }

    .auth-card {
      width: 100%;
      max-width: 420px;
      padding: 32px 28px 24px;
      border-radius: 14px !important;
      box-shadow: 0 8px 32px rgba(0,0,0,.18) !important;
      border-top: 4px solid var(--chareety-accent);
    }

    .auth-logo {
      text-align: center;
      margin-bottom: 28px;

      img { height: 40px; }

      h2 {
        font-size: 22px;
        font-weight: 700;
        color: var(--chareety-text);
        margin: 10px 0 4px;
      }

      p {
        font-size: 13px;
        color: var(--chareety-muted);
        margin: 0;
      }
    }

    .form-group {
      margin-bottom: 16px;
    }

    .full-width {
      width: 100%;
    }

    button {
      margin-top: 8px;
      height: 44px;
    }

    .btn-spinner {
      display: inline-block;
      margin-right: 8px;
    }

    .auth-link-row {
      text-align: center;
      font-size: 13px;
      color: var(--chareety-muted);
      margin-top: 12px;

      a { color: var(--chareety-accent-dark); font-weight: 500; }
    }

    .consent-section {
      margin: 8px 0 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .consent-section mat-checkbox {
      font-size: 13px;
      line-height: 1.4;
    }

    .consent-section a {
      color: var(--chareety-accent-dark, #d4682e);
      font-weight: 500;
      text-decoration: underline;
    }

    .consent-section a:hover {
      color: var(--chareety-primary, #2B7CB8);
    }

    .consent-error {
      color: #b00020;
      font-size: 11px;
      margin: -4px 0 4px 32px;
    }

    .verification-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      gap: 20px;
    }

    .verification-message {
      font-size: 16px;
      color: var(--chareety-text);
      margin: 0;
      font-weight: 500;
    }

    .verification-error {
      padding: 24px;
      background: #ffebee;
      border: 1px solid #ef5350;
      border-radius: 8px;
      text-align: center;

      mat-icon {
        color: #d32f2f;
        font-size: 48px;
        width: 48px;
        height: 48px;
        margin-bottom: 12px;
      }

      p {
        color: #d32f2f;
        font-size: 15px;
        margin: 0 0 16px;
        line-height: 1.5;
      }

      button {
        margin-top: 8px;
      }
    }
  `]
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm: FormGroup;
  loading = false;
  isVerifying = false;
  verificationOrgId: string | null = null;
  verificationToken: string | null = null;
  verificationError: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private api: OnboardingApiService,
    private stateService: StateManagementService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar
  ) {
    this.registerForm = this.fb.group({
      orgName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
      agreeTerms: [false, Validators.requiredTrue],
      agreeOrgContract: [false, Validators.requiredTrue],
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        // Handle verification link
        if (params['verify'] === '1' && params['orgId'] && params['token']) {
          this.handleVerificationLink(params['orgId'], params['token']);
          return;
        }

        // Pre-fill from partner referral link
        const orgName = params['org'] || '';
        const email = params['email'] || '';
        const contact = params['contact'] || '';
        const phone = params['phone'] || '';
        const ref = params['ref'] || '';
        const rid = params['rid'] || '';
        const pid = params['pid'] || '';

        if (orgName || email || ref || rid) {
          if (orgName) this.registerForm.patchValue({ orgName });
          if (email) this.registerForm.patchValue({ email });

          this.stateService.setPartnerPrefill({
            orgName,
            email,
            contactName: contact,
            phone,
            referralCode: ref,
          });

          if (rid) this.stateService.setReferralId(rid);
          if (pid) this.stateService.setPartnerId(pid);
          if (ref) this.stateService.setSource('partner');
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async handleVerificationLink(orgId: string, token: string): Promise<void> {
    this.isVerifying = true;
    this.verificationOrgId = orgId;
    this.verificationToken = token;
    this.verificationError = null;

    try {
      // Call the verification API
      console.log('Calling verifyOrgAuth API...');
      const result = await this.api.verifyOrgAuth(orgId, token).toPromise();
      console.log('verifyOrgAuth result:', result);

      if (result?.success) {
        console.log('Verification successful! AuthVerifiedAt is now set in Firestore.');
        this.snackBar.open('Organization verified! Proceeding to onboarding...', 'Close', { duration: 3000 });

        // Redirect to onboarding flow
        console.log('Navigating to /onboard/' + orgId);
        setTimeout(() => {
          this.router.navigate(['/onboard', orgId]);
        }, 500);
      } else {
        console.log('Verification returned invalid result:', result);
        this.verificationError = 'Verification failed. Please try again or contact support.';
        this.snackBar.open(this.verificationError!, 'Close', { duration: 5000 });
        this.isVerifying = false;
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      this.verificationError = error.message || 'Verification failed. The link may have expired.';
      this.snackBar.open(this.verificationError!, 'Close', { duration: 5000 });
      this.isVerifying = false;
    }
  }

  /** Safe template helper — avoids optional chaining in templates */
  hasError(controlName: string, errorName: string): boolean {
    const control = this.registerForm.get(controlName);
    return control !== null && control.hasError(errorName);
  }

  passwordMatchValidator(group: FormGroup): { [key: string]: any } | null {
    const password = group.get('password');
    const confirmPassword = group.get('confirmPassword');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      return { 'passwordMismatch': true };
    }
    return null;
  }

  async onSubmit(): Promise<void> {
    if (!this.registerForm.valid) return;

    this.loading = true;
    try {
      const { orgName, email, password } = this.registerForm.value;
      const orgId = await this.authService.registerOrganization(orgName, email, password, {
        termsAcceptedAt: new Date().toISOString(),
        msaAcceptedAt: new Date().toISOString(),
        msaVersion: '2026-05-18-v1',
      });

      this.snackBar.open('Account created! Please log in to continue.', 'Close', { duration: 5000 });
      await this.router.navigate(['/login'], { queryParams: { email } });
    } catch (error: any) {
      this.snackBar.open(error.message || 'Registration failed', 'Close', { duration: 5000 });
    } finally {
      this.loading = false;
    }
  }
}
